import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PartyPopper, Clock } from 'lucide-react';
import {
  formatHappyHourTime,
  getTargetCategoryName,
  type HappyHour,
} from '../services/happyHourApi';
import type { Category } from '../types';

interface HappyHourBannerProps {
  happyHour: HappyHour;
  categories: Category[];
  /** Appelé quand le happy hour est censé être terminé (pour resync) */
  onExpired?: () => void;
}

/** Seuil sous lequel on affiche un compte à rebours d'urgence (minutes) */
const URGENT_THRESHOLD_MIN = 15;

/** Minutes écoulées depuis minuit, heure locale */
function nowInMinutes(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * v5 — Bandeau Happy Hour affiché en haut du menu.
 *
 * Ex: 🎉 HAPPY HOUR — -20% sur les Desserts — jusqu'à 19h00
 *
 * Si la fin est proche (< 15 min), affiche un compte à rebours d'urgence.
 */
export const HappyHourBanner: React.FC<HappyHourBannerProps> = ({
  happyHour,
  categories,
  onExpired,
}) => {
  const { t } = useTranslation();
  const [minutesLeft, setMinutesLeft] = useState<number>(
    () => happyHour.end_time - nowInMinutes()
  );

  // Recalcul chaque minute (le backend reste la référence via le polling 5 min)
  useEffect(() => {
    const tick = () => {
      const left = happyHour.end_time - nowInMinutes();
      setMinutesLeft(left);
      if (left <= 0 && onExpired) onExpired();
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [happyHour.end_time, onExpired]);

  const categoryName = getTargetCategoryName(happyHour, categories);
  const endLabel = formatHappyHourTime(happyHour.end_time);
  const isUrgent = minutesLeft > 0 && minutesLeft <= URGENT_THRESHOLD_MIN;

  // "-20% sur les Desserts" ou "-20% sur tout le menu"
  const scopeLabel = categoryName
    ? t('sur les {{category}}', { category: categoryName })
    : t('sur tout le menu');

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="relative overflow-hidden rounded-2xl px-5 py-3 mb-4 shadow-lg"
      style={{
        background:
          'linear-gradient(100deg, var(--color-primary, #F97316), var(--color-secondary, #FB923C))',
      }}
      role="status"
    >
      {/* Reflet animé subtil */}
      <div className="happy-hour-shine pointer-events-none absolute inset-0" />

      <div className="relative flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-white text-center">
        <span className="flex items-center gap-2 font-extrabold tracking-wide text-lg">
          <PartyPopper size={22} className="shrink-0" />
          {t('HAPPY HOUR')}
        </span>

        <span className="hidden sm:inline text-white/60">—</span>

        <span className="font-bold text-lg">
          −{happyHour.discount_percent}% {scopeLabel}
        </span>

        <span className="hidden sm:inline text-white/60">—</span>

        {isUrgent ? (
          <span className="flex items-center gap-1.5 font-bold bg-white/25 rounded-full px-3 py-0.5 animate-pulse">
            <Clock size={16} />
            {t('plus que {{count}} min !', { count: minutesLeft })}
          </span>
        ) : (
          <span className="font-semibold text-white/95">
            {t("jusqu'à {{time}}", { time: endLabel })}
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default HappyHourBanner;
